import { useMemo, useState } from 'react';
import clsx from 'clsx';
import BottomDrawer from '../../../../../../../common/_custom/BottomDrawer/BottomDrawer';
import useFetchFilteredViewData from '../../../../../../../common/FilteredViewsButton/useFetchFilteredViewData';
import { GREY_DROPDOWN_CARET, PURPLE_CHECK_SMALL } from '../../../../../../../assets/hostedassets';
import SearchIcon from '../../../../../../../assets/Svg/SearchIcon';
import styles from './MobileViewSheet.module.scss';

type FilteredViewOption = {
    label: string;
    value: string;
    endLabel?: string;
    columns?: string[];
};

interface MobileViewSheetProps {
    source: string;
    selectedView: FilteredViewOption | null;
    onViewSelect: (filters: Record<string, any>, columns?: string[]) => void;
    onSelectedViewChange: (view: FilteredViewOption | null) => void;
    onFiltersApply: (filters: Record<string, any>) => void;
    onFilterValuesUpdate?: (filters: Record<string, any>) => void;
    onClear?: () => void;
}

function MobileViewSheet({
    source,
    selectedView,
    onViewSelect,
    onSelectedViewChange,
    onFiltersApply,
    onFilterValuesUpdate,
    onClear,
}: MobileViewSheetProps) {
    const [showSheet, setShowSheet] = useState(false);
    const [searchText, setSearchText] = useState('');
    const { filteredViews, isLoading, filterMapMemo } = useFetchFilteredViewData(source);

    const visibleViews = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) return filteredViews;
        return filteredViews.filter((view) => view.label.toLowerCase().includes(query));
    }, [filteredViews, searchText]);

    const handleSelect = (view: FilteredViewOption) => {
        onSelectedViewChange(view);
        const filterData = filterMapMemo.get(view.value);
        if (filterData) {
            onViewSelect(filterData.filters || {}, view.columns);
            onFiltersApply(filterData.filters || {});
            onFilterValuesUpdate?.(filterData.filters || {});
        }
        setShowSheet(false);
        setSearchText('');
    };

    const handleCloseSheet = () => {
        setShowSheet(false);
        setSearchText('');
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectedViewChange(null);
        onViewSelect({}, []);
        onFiltersApply({});
        onFilterValuesUpdate?.({});
        onClear?.();
    };

    return (
        <>
            <button
                className={clsx(styles.ViewButton, {
                    [styles.ViewActive]: !!selectedView,
                })}
                onClick={() => setShowSheet(true)}
            >
                <span className={styles.ViewLabel}>{selectedView?.label || 'Select View'}</span>
                {selectedView ? (
                    <span className={styles.ClearIcon} onClick={handleClear}>
                        ✕
                    </span>
                ) : (
                    <img src={GREY_DROPDOWN_CARET} width={16} height={16} alt="" />
                )}
            </button>

            {showSheet && (
                <BottomDrawer onClose={handleCloseSheet} height="70%" showHeading heading="Select View">
                    {!isLoading && filteredViews.length > 0 && (
                        <div className={styles.SearchWrapper}>
                            <input
                                type="text"
                                className={styles.SearchInput}
                                placeholder="Search views"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                aria-label="Search views"
                                spellCheck={false}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                autoFocus
                            />
                            <SearchIcon className={styles.SearchIcon} />
                        </div>
                    )}
                    <div className={styles.Body}>
                        {isLoading ? (
                            <div className={styles.Loading}>Loading views...</div>
                        ) : filteredViews.length === 0 ? (
                            <div className={styles.Empty}>No saved views</div>
                        ) : visibleViews.length === 0 ? (
                            <div className={styles.Empty}>No views match "{searchText}"</div>
                        ) : (
                            visibleViews.map((view) => (
                                <button
                                    key={view.value}
                                    className={clsx(styles.ViewItem, {
                                        [styles.ViewItemActive]: selectedView?.value === view.value,
                                    })}
                                    onClick={() => handleSelect(view)}
                                >
                                    <span className={styles.ViewItemLabelGroup}>
                                        <span className={styles.ViewItemLabel}>{view.label}</span>
                                        {view.endLabel && (
                                            <span className={styles.ViewItemCreator} title={view.endLabel}>
                                                {view.endLabel}
                                            </span>
                                        )}
                                    </span>
                                    {selectedView?.value === view.value && <img src={PURPLE_CHECK_SMALL} width={16} height={16} alt="" />}
                                </button>
                            ))
                        )}
                    </div>
                </BottomDrawer>
            )}
        </>
    );
}

export default MobileViewSheet;
